<template>
  <!-- processing card -->
  <DescriptiveLoader
    v-if="processing"
    message="Power Trip is updating your vehicles saved settings"
    class="bg-pwt_snow"
  />
  <!-- save successful card -->
  <v-card flat class="pa-5 bg-pwt_snow" v-else-if="saveStatus === 'success'">
    <v-alert type="success" class="mx-2">
      Vehicle updated successfully and saved to the database
    </v-alert>
  </v-card>
  <!-- save unsuccessful card -->
  <v-card flat class="pa-5 bg-pwt_snow" v-else-if="saveStatus === 'failed'">
    <v-alert type="error" class="mx-2">
      Vehicle was updated locally but failed to be saved to the database
    </v-alert>
  </v-card>
  <!-- vehicle details card -->
  <v-card flat class="pa-5 bg-pwt_snow" v-else>
    <VehicleBookingBand v-if="vehicle && vehicle.directusId" :vehicle="vehicle" />
    <!-- not connected vehicle notif -->
    <NoLiveDataNotif
      v-if="vehicle && vehicle.orakle_update_error"
      :orakle_update_error="vehicle.orakle_update_error"
    />
    <!-- vehicle card -->
    <v-card class="py-4 pl-md-12 rounded-lg bg-pwt_snow mb-10" flat>
      <v-row class="flex-column flex-md-row">
        <v-col md="3">
          <!-- image section -->
          <v-img
            contain
            :src="imageSrc()"
            aspect-ratio="1"
            :width="mdAndUp ? '100%' : '40%'"
            :height="mdAndUp ? '100%' : 'auto'"
            style="margin: auto"
          ></v-img>
        </v-col>
        <v-col md="9" class="position-relative">
          <!-- details section -->
          <div v-if="vehicle">
            <v-card-title class="d-flex align-center pb-0">
              {{
                vehicle.vehicleType === VehicleType.GENERIC_VEHICLE
                  ? 'Generic EV'
                  : (vehicle.licensePlate ?? 'unrecorded license plate')
              }}
            </v-card-title>
            <v-card-subtitle class="font-weight-medium">
              {{ vehicle.evModel?.name ?? 'unknown modal' }}
            </v-card-subtitle>
            <v-card-subtitle
              v-if="vehicle.vehicleType !== VehicleType.GENERIC_VEHICLE"
              class="font-weight-medium"
            >
              {{ vehicle.name ?? 'unnamed vehicle' }}
            </v-card-subtitle>
            <v-card-actions class="px-4">
              <CustomTextButton> Edit vehicle photo </CustomTextButton>
            </v-card-actions>
          </div>
          <div v-else>
            <v-row no-gutters class="mb-4">
              <v-skeleton-loader type="heading" width="70%" />
              <v-skeleton-loader type="heading" width="30%" />
            </v-row>
            <v-skeleton-loader type="text" width="40%" />
            <v-skeleton-loader type="text" width="70%" class="mb-4" />
            <v-skeleton-loader type="heading" width="100%" />
          </div>
        </v-col>
      </v-row>
    </v-card>
    <!-- settings section -->
    <v-form :disabled="processing || loading">
      <!-- general details -->
      <v-card-title class="pl-0 mb-3"> General details </v-card-title>
      <v-card class="py-4 px-12 rounded-lg bg-pwt_snow mb-10" elevation="1">
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center"> Plate number </v-col>
          <v-col md="8" align-self="center">
            <v-text-field v-model="plate" bg-color="transparent" clearable @change="flagAsDirty" />
          </v-col>
        </v-row>
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center"> Make & model </v-col>
          <v-col md="8" align-self="center">
            <v-autocomplete
              v-if="fuelType === 'Electric' || fuelType === 'Hydrogen'"
              v-model="makeAndModel"
              :items="models"
              item-title="name"
              label="EV make/model (optional)"
              :menu-props="{ contentClass: 'pwt-scrollbar-styles' }"
              return-object
              clearable
              bg-color="transparent"
              @change="handleModelSelect"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center"> Vehicle name </v-col>
          <v-col md="8" align-self="center">
            <v-text-field v-model="name" bg-color="transparent" clearable @change="flagAsDirty" />
          </v-col>
        </v-row>
        <v-row class="flex-column flex-md-row mb-5">
          <v-col md="4" align-self="center"> Added by </v-col>
          <v-col md="8" align-self="center"> {{ vehicle?.created_by }}</v-col>
        </v-row>
      </v-card>
      <v-card class="py-4 px-12 rounded-lg bg-pwt_snow mb-10" elevation="1">
        <!-- vehicle weight -->
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center">
            <p>
              Vehicle Weight
              <StyledToolTip v-if="vehicleWeightTooltip" :data="vehicleWeightTooltip" />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-text-field
              v-model="vehicleWeight"
              type="number"
              hide-spin-buttons
              suffix="kg"
              :rules="[validateNotNan, validateNotNegative, validateNotZero]"
              @change="flagAsDirty"
              bg-color="transparent"
            />
          </v-col>
        </v-row>
        <!-- fuel type -->
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="start">
            <p>
              Energy source
              <StyledToolTip v-if="fuelTypeTooltip" :data="fuelTypeTooltip" />
            </p>
          </v-col>
          <v-col cols="12" md="8" align-self="center">
            <v-row no-gutters>
              <v-col cols="6" md="3">
                <v-btn
                  class="text-caption rounded mr-1 mb-1"
                  :class="{ 'btn-border': fuelType !== 'Electric' }"
                  flat
                  :color="fuelType === 'Electric' ? 'pwt_azure_20' : undefined"
                  @click="() => (fuelType = 'Electric')"
                  style="width: 95%"
                >
                  <template v-slot:prepend>
                    <v-icon icon="custom:offlineBolt" color="primary" />
                  </template>
                  Electric
                </v-btn>
              </v-col>
              <v-col cols="6" md="3">
                <v-btn
                  class="text-caption rounded mr-1 mb-1"
                  :class="{ 'btn-border': fuelType !== 'Hybrid' }"
                  flat
                  :color="fuelType === 'Hybrid' ? 'pwt_azure_20' : undefined"
                  @click="() => (fuelType = 'Hybrid')"
                  style="width: 95%"
                >
                  <template v-slot:prepend>
                    <v-icon icon="custom:waterEc" color="primary" />
                  </template>
                  Hybrid
                </v-btn>
              </v-col>
              <v-col cols="6" md="3">
                <v-btn
                  class="text-caption rounded mr-1 mb-1"
                  :class="{ 'btn-border': fuelType !== 'Plug in hybrid' }"
                  flat
                  :color="fuelType === 'Plug in hybrid' ? 'pwt_azure_20' : undefined"
                  @click="() => (fuelType = 'Plug in hybrid')"
                  style="width: 95%"
                >
                  <template v-slot:prepend>
                    <v-icon icon="custom:waterEc" color="primary" />
                  </template>
                  PHEV
                </v-btn>
              </v-col>
              <v-col cols="6" md="3">
                <v-btn
                  class="text-caption rounded mr-1 mb-1"
                  :class="{ 'btn-border': fuelType !== 'Petrol' }"
                  flat
                  :color="fuelType === 'Petrol' ? 'pwt_azure_20' : undefined"
                  @click="() => (fuelType = 'Petrol')"
                  style="width: 95%"
                >
                  <template v-slot:prepend>
                    <v-icon icon="custom:waterDrop" color="primary" />
                  </template>
                  Petrol
                </v-btn>
              </v-col>
              <v-col cols="6" md="3">
                <v-btn
                  class="text-caption rounded mr-1 mb-1"
                  :class="{ 'btn-border': fuelType !== 'Diesel' }"
                  flat
                  :color="fuelType === 'Diesel' ? 'pwt_azure_20' : undefined"
                  @click="() => (fuelType = 'Diesel')"
                  style="width: 95%"
                >
                  <template v-slot:prepend>
                    <v-icon icon="custom:waterDrop" color="primary" />
                  </template>
                  Diesel
                </v-btn>
              </v-col>
              <v-col cols="6" md="3">
                <v-btn
                  class="text-caption rounded mr-1 mb-1"
                  :class="{ 'btn-border': fuelType !== 'Hydrogen' }"
                  flat
                  :color="fuelType === 'Hydrogen' ? 'pwt_azure_20' : undefined"
                  @click="() => (fuelType = 'Hydrogen')"
                  style="width: 95%"
                >
                  <template v-slot:prepend>
                    <v-icon icon="custom:hydrogen" color="primary" />
                  </template>
                  Hydrogen
                </v-btn>
              </v-col>
              <v-col cols="6" md="3">
                <v-btn
                  class="text-caption rounded mr-1 mb-1"
                  :class="{ 'btn-border': fuelType !== 'Other' }"
                  flat
                  :color="fuelType === 'Other' ? 'pwt_azure_20' : undefined"
                  @click="() => (fuelType = 'Other')"
                  style="width: 95%"
                >
                  <template v-slot:prepend>
                    <v-icon icon="custom:waterDrop" color="primary" />
                  </template>
                  Other
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <!-- if ev battery size -->
        <v-row
          v-if="fuelType === 'Electric' || fuelType === 'Plug in hybrid'"
          class="flex-column flex-md-row"
        >
          <v-col md="4" align-self="center">
            <p>
              Battery Size
              <StyledToolTip v-if="batterySizeTooltip" :data="batterySizeTooltip" />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-text-field
              v-model="batterySize"
              type="number"
              clearable
              :rules="[validateNotNan, validateNotNegative, validateNotZero]"
              suffix="kWh"
              @change="flagAsDirty"
              bg-color="transparent"
            />
          </v-col>
        </v-row>
      </v-card>
      <!-- Charging section-->
      <template v-if="fuelType === 'Electric' || fuelType === 'Plug in hybrid'">
        <v-card-title class="pl-0 mb-3"> Charging </v-card-title>
        <v-card class="py-4 px-12 rounded-lg bg-pwt_snow mb-10" elevation="1">
          <!-- AC (slow) charging speed/AC max kW rating -->
          <v-row class="flex-column flex-md-row">
            <v-col md="4" align-self="center">
              <p>
                AC (slow) charging speed
                <StyledToolTip v-if="acChargingSpeedTooltip" :data="acChargingSpeedTooltip" />
              </p>
            </v-col>
            <v-col md="8" align-self="center">
              <v-text-field
                v-model="acRating"
                type="number"
                hide-spin-buttons
                suffix="kW"
                :rules="[validateNotNan, validateNotNegative, validateNotZero]"
                @change="flagAsDirty"
                bg-color="transparent"
              />
            </v-col>
          </v-row>
          <!-- DC (slow) charging speed/DC max kW rating -->
          <v-row class="flex-column flex-md-row">
            <v-col md="4" align-self="center">
              <p>
                DC (slow) charging speed
                <StyledToolTip v-if="dcChargingSpeedTooltip" :data="dcChargingSpeedTooltip" />
              </p>
            </v-col>
            <v-col md="8" align-self="center">
              <v-text-field
                v-model="dcRating"
                type="number"
                hide-spin-buttons
                suffix="kW"
                :rules="[validateNotNan, validateNotNegative, validateNotZero]"
                @change="flagAsDirty"
                bg-color="transparent"
              />
            </v-col>
          </v-row>
          <!-- Connectors selection -->
          <v-row class="flex-column flex-md-row mb-10">
            <v-col md="4" align-self="start">
              <p>
                Compatible connectors
                <StyledToolTip v-if="connectorSettingsTooltip" :data="connectorSettingsTooltip" />
              </p>
            </v-col>
            <v-col md="8" align-self="start">
              <v-autocomplete
                :items="[...connectors, ...cables]"
                item-title="displayName"
                @change="flagAsDirty"
                @update:model-value="(value) => selectedConnectors.push(value)"
                bg-color="transparent"
              />
              <v-row class="flex-column flex-md-row">
                <div
                  v-for="(connector, index) in selectedConnectors"
                  class="btn-border rounded d-flex align-center mr-2 mb-2 pa-2"
                >
                  <img
                    :src="connector.iconURL ? connector.iconURL : fallbackImgSrc()"
                    width="26"
                    height="26"
                  />
                  <span class="mx-2">{{ connector.displayName ?? connector.standard }}</span>
                  <v-btn
                    icon
                    flat
                    color="transparent"
                    density="compact"
                    @click="() => selectedConnectors.splice(index, 1)"
                  >
                    <v-icon icon="mdi-close" color="pwt_medium_grey" />
                  </v-btn>
                </div>
              </v-row>
            </v-col>
          </v-row>
        </v-card>
      </template>
      <!-- Additional load -->
      <v-card-title class="pl-0 mb-3"> Additional load </v-card-title>
      <v-card class="py-4 px-12 rounded-lg bg-pwt_snow mb-10" elevation="1">
        <!-- additional weight -->
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center">
            <p>
              Additional Weight
              <StyledToolTip v-if="additionalWeightTooltip" :data="additionalWeightTooltip" />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-text-field
              v-model="additionalWeight"
              type="number"
              hide-spin-buttons
              suffix="kg"
              :rules="[validateNotNan, validateNotNegative]"
              @change="flagAsDirty"
              bg-color="transparent"
            />
          </v-col>
        </v-row>
      </v-card>
      <!-- physics profile section -->
      <v-card-title class="pl-0 mb-3"> Efficiency & adjustments </v-card-title>
      <v-card
        class="py-4 px-12 rounded-lg bg-pwt_snow mb-10"
        elevation="1"
        v-if="fuelType === 'Electric' || fuelType === 'Plug in hybrid' || fuelType === 'Hydrogen'"
      >
        <!-- if ev range adjustment/SoH slider -->
        <v-row v-if="fuelType === 'Electric'" class="mb-5 flex-column flex-md-row">
          <v-col md="4" align-self="center">
            <p>
              Range Adjustment
              <StyledToolTip v-if="sohToolTipContent" :data="sohToolTipContent" />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-slider
              v-model="SoH"
              :label="SoH + '%'"
              min="1"
              max="100"
              track-color="grey-lighten-2"
              :messages="batteryAgeMessage()"
              @end="flagAsDirty"
              :disabled="processing || loading"
              step="1"
              color="primary"
              thumb-size="16"
              track-size="6"
            />
          </v-col>
        </v-row>
        <!-- Aerodynamics slider -->
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center">
            <p>
              Aerodynamics (drag)
              <StyledToolTip v-if="dragTooltip" :data="dragTooltip" />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-slider
              v-model="aerodynamics"
              :label="(aerodynamics / 100).toFixed(5)"
              min="10"
              max="1000"
              track-color="grey-lighten-2"
              hide-details
              @end="flagAsDirty"
              :disabled="processing || loading"
              step="1"
              color="primary"
              thumb-size="16"
              track-size="6"
            />
          </v-col>
        </v-row>
        <!-- Tyre dynamics slider -->
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center">
            <p>
              Tyre dynamics (grip)
              <StyledToolTip v-if="gripTooltip" :data="gripTooltip" />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-slider
              v-model="tyreDynamics"
              :label="(tyreDynamics / 1000).toString()"
              min="2"
              max="20"
              track-color="grey-lighten-2"
              hide-details
              @end="flagAsDirty"
              :disabled="processing || loading"
              step="1"
              color="primary"
              thumb-size="16"
              track-size="6"
            />
          </v-col>
        </v-row>
        <!-- Regenerative breaking efficiency slider -->
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center">
            <p>
              Regenerative braking
              <StyledToolTip
                v-if="regenerativeBrakingEfficiencyTooltip"
                :data="regenerativeBrakingEfficiencyTooltip"
              />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-slider
              v-model="regenerativeBraking"
              :label="regenerativeBraking + '%'"
              min="1"
              max="100"
              track-color="grey-lighten-2"
              hide-details
              @end="flagAsDirty"
              :disabled="processing || loading"
              step="1"
              color="primary"
              thumb-size="16"
              track-size="6"
            />
          </v-col>
        </v-row>
        <!-- Power train efficiency slider -->
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center">
            <p>
              Power train efficiency
              <StyledToolTip
                v-if="powerTrainEfficiencyTooltip"
                :data="powerTrainEfficiencyTooltip"
              />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-slider
              v-model="powerTrain"
              :label="powerTrain + '%'"
              min="1"
              max="100"
              track-color="grey-lighten-2"
              hide-details
              @end="flagAsDirty"
              :disabled="processing || loading"
              step="1"
              color="primary"
              thumb-size="16"
              track-size="6"
            />
          </v-col>
        </v-row>
                <!-- Energy used when stopped in kW -->
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center">
            <p>
              Energy used when stopped
              <StyledToolTip
                v-if="energyUsedWhenStoppedTooltip"
                :data="energyUsedWhenStoppedTooltip"
              />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-text-field
              v-model="energyUsedWhenStopped"
              type="number"
              hide-spin-buttons
              suffix="kW"
              :rules="[validateNotNan, validateNotNegative]"
              @change="flagAsDirty"
              bg-color="transparent"
            />
          </v-col>
        </v-row>
        <!-- Energy used when moving in kW -->
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center">
            <p>
              Energy used when moving
              <StyledToolTip
                v-if="energyUsedWhenMovingTooltip"
                :data="energyUsedWhenMovingTooltip"
              />
            </p>
          </v-col>
          <v-col md="8" align-self="center">
            <v-text-field
              v-model="energyUsedWhenMoving"
              type="number"
              hide-spin-buttons
              suffix="kW"
              :rules="[validateNotNan, validateNotNegative]"
              @change="flagAsDirty"
              bg-color="transparent"
            />
          </v-col>
        </v-row>
      </v-card>
    </v-form>
    <!-- Actions section -->
    <v-card-actions>
      <v-spacer></v-spacer>
      <TextBtn :disabled="!(vehicle && vehicle.evModel)" @click="handelReset">
        Reset to vehicle defaults
      </TextBtn>
      <ElevatedBtn :disabled="!dirty" @click="saveData"> Save changes </ElevatedBtn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts" setup>
import EVModel from '@/classes/vehicle_classes/evModel'
import Vehicle from '@/classes/vehicle_classes/vehicle'
import CustomTextButton from '@/components/ui-elements/buttons/custom-buttons/CustomTextButton.vue'
import {
  type ConnectorDetailsData,
  connectorDetailsDataMap,
  type ConnectorDetailsIconData,
  getAllSocketedConnectors,
  getAllTetheredConnectors,
} from '@/data/connectorDetailsData'
import {
  ActionTypes,
  GettersTypes,
  ManagedContentNames,
  MutationTypes,
  type State,
} from '@/store/store_types'
import type { ConnectorFormat, ConnectorType } from '@/types/charger_Db_types'
import type { DirectusAdvancedConfig, DirectusUserSelectedPlug } from '@/types/directus-types'
import type VehicleData from '@/types/directus-types/directus_vehicle.ts'
import type { FuelType } from '@/types/sheared_local_types'
import { VehicleType } from '@/types/vehicle_specific_types.ts'
import getAssetSrc from '@/utils/getAssetSrc'
import parseIntOrFloat from '@/utils/parseIntOrFloat'
import to2DP from '@/utils/to2DP'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import ElevatedBtn from '../../ui-elements/buttons/ElevatedBtn.vue'
import TextBtn from '../../ui-elements/buttons/TextBtn.vue'
import StyledToolTip from '../../ui-elements/displays/StyledToolTip.vue'
import DescriptiveLoader from '../../ui-elements/loaders/DescriptiveLoader.vue'
import NoLiveDataNotif from '../NoLiveDataNotif.vue'
import VehicleBookingBand from '../VehicleBookingBand.vue'

const legacy_store = useStore<State>()
const vehicle = computed<Vehicle | undefined>(
  () => legacy_store.getters[GettersTypes.displayedVehicle],
)

interface LocalConnectorData {
  iconURL?: string
  displayName: string
  standard: ConnectorType
  format: ConnectorFormat
}

const { smAndDown, mdAndUp } = useDisplay()

const store = useStore<State>()
// control refs
const dirty = ref(false)
const loading = ref(true)
const processing = ref(false)
const saveStatus = ref<undefined | 'failed' | 'success' | undefined>(undefined)
// identification refs
const name = ref<string | null>(null)
const plate = ref<string | null>(null)
// weights refs
const vehicleWeight = ref(1000)
const additionalWeight = ref(0)
// fuel refs
const fuelType = ref<FuelType>('Electric')
const batterySize = ref(25)
const SoH = ref(100)
// charging refs
const acRating = ref(10)
const dcRating = ref(50)
// energy refs
const energyUsedWhenStopped = ref(0)
const energyUsedWhenMoving = ref(0)
// physics profile refs
const models = computed(() => legacy_store.state.evModels)
const makeAndModel = ref<EVModel | null>(null)
const aerodynamics = ref(50)
const tyreDynamics = ref(50)
const regenerativeBraking = ref(50)
const powerTrain = ref(50)
// connectors refs
const selectedConnectors = ref<LocalConnectorData[]>([])
// tooltip content
const sohToolTipContent = computed(() =>
  store.state.managedContent.find((content) => content.Name === ManagedContentNames.SoHToolTip),
)
const vehicleWeightTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'vehicle details vehicle weight tooltip',
  ),
)
const additionalWeightTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'vehicle details additional weight tooltip',
  ),
)
const fuelTypeTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'vehicle details fuel type tooltip',
  ),
)
const batterySizeTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model battery size tooltip',
  ),
)
const acChargingSpeedTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model ac charging speed tooltip',
  ),
)
const dcChargingSpeedTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model dc charging speed tooltip',
  ),
)
const energyUsedWhenStoppedTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model energy used when stopped tooltip',
  ),
)
const energyUsedWhenMovingTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model additional energy used when moving tooltip',
  ),
)
const dragTooltip = computed(() =>
  store.state.managedContent.find((content) => content.Description === 'ev model drag tooltip'),
)
const gripTooltip = computed(() =>
  store.state.managedContent.find((content) => content.Description === 'ev model grip tooltip'),
)
const regenerativeBrakingEfficiencyTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model regenerative braking efficiency tooltip',
  ),
)
const powerTrainEfficiencyTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model power train efficiency tooltip',
  ),
)
const connectorSettingsTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model connector settings tooltip',
  ),
)
// cables and connectors

/**
 * Orders the given array of LocalConnectorData objects based on whether they are selected or not.
 *
 * @param {LocalConnectorData[]} connectors - The array of LocalConnectorData objects to order.
 * @return {LocalConnectorData[]} The ordered array of LocalConnectorData objects.
 */
function orderConnectors(connectors: LocalConnectorData[]): LocalConnectorData[] {
  const returnArray: LocalConnectorData[] = []
  connectors.forEach((connector) => {
    if (connectorIsSelected(connector)) {
      returnArray.unshift(connector)
    } else {
      returnArray.push(connector)
    }
  })
  return returnArray
}

/**
 * Checks if a given connector is already selected.
 *
 * @param {LocalConnectorData} connector - The connector to check for selection.
 * @return {boolean} True if the connector is selected, false otherwise.
 */
function connectorIsSelected(connector: LocalConnectorData): boolean {
  return !!selectedConnectors.value.find(
    (selectedConnector) =>
      selectedConnector.standard === connector.standard &&
      selectedConnector.format === connector.format,
  )
}

/**
 * Converts ConnectorDetailsData to LocalConnectorData.
 *
 * @param {ConnectorDetailsData} connectorData - The connector data to be converted.
 * @return {LocalConnectorData} The converted local connector data.
 */
function convertConnectorDataToLocalConnectorData(
  connectorData: ConnectorDetailsData,
): LocalConnectorData {
  return {
    displayName: connectorData.displayName,
    format: 'CABLE',
    standard: connectorData.standard,
    iconURL: getSrc(connectorData.iconURL, 'cable'),
  }
}

/**
 * Returns the target connectors icon asset url if one exist
 *
 * @param iconUrlData the 'ConnectorDetailsData' `iconUrl` property.
 * @param format the target format
 * @returns complete file path if it exist or undefined if not.
 */
function getSrc(
  iconUrlData: string | ConnectorDetailsIconData | undefined,
  format: 'cable' | 'socket',
): string | undefined {
  let partialFilePath: string | undefined = undefined
  if (!iconUrlData) return undefined
  if (typeof iconUrlData === 'string') partialFilePath = iconUrlData
  if (typeof iconUrlData === 'object') {
    if (Object.hasOwn(iconUrlData, format)) partialFilePath = iconUrlData[format]
  }
  if (!partialFilePath) return undefined
  return getAssetSrc(partialFilePath)
}

/** List of all possible hard point connectors */
const connectors = computed(() => {
  // convert connector data to locally used connector data objs and order selected connectors at the start of the list.
  return orderConnectors(
    fuelType.value === 'Hydrogen' && connectorDetailsDataMap.get('X_PHONY_HYDROGEN')
      ? [convertConnectorDataToLocalConnectorData(connectorDetailsDataMap.get('X_PHONY_HYDROGEN')!)]
      : getAllTetheredConnectors().map((connector) =>
          convertConnectorDataToLocalConnectorData(connector),
        ),
  )
})
/** List of all possible cables */
const cables = computed(() => {
  // convert connector data to locally used connector data objs and order selected connectors at the start of the list.
  return orderConnectors(
    getAllSocketedConnectors().map((connector) =>
      convertConnectorDataToLocalConnectorData(connector),
    ),
  )
})

// Form validation

/**
 * Validates the fuel type selected.
 *
 * @returns {true | string} Returns true if the fuel type is valid, otherwise returns an error message.
 */
function validateFuelType(): true | string {
  // Check if the fuel type is one of the valid options
  const isValidFuelType =
    fuelType.value === 'Electric' ||
    fuelType.value === 'Petrol' ||
    fuelType.value === 'Diesel' ||
    fuelType.value === 'Hybrid'

  // Return true if the fuel type is valid, otherwise return an error message
  return isValidFuelType ? true : 'Please select a fuel type'
}

/**
 * Validates that the input value is not negative.
 *
 * @param {number|string|null} value - The value to be validated.
 * @return {true|string} Returns true if the value is not negative, otherwise returns an error message.
 */
function validateNotNegative(value: number | string | null): true | string {
  if (value) {
    // Validate that the value is not negative.
    const parsedNumber = parseIntOrFloat(value)
    if (parsedNumber === 0) return true // prevent false negative from 0 being valid but not truthy.
    if (parsedNumber && parsedNumber >= 0) {
      return true
    } else {
      return 'Please enter a value greater than or equal to 0'
    }
  } else {
    return 'Please enter a value'
  }
}

/**
 * Validates that the input value is not NaN.
 *
 * @param {number|string|null} value - The value to be validated.
 * @return {true|string} Returns true if the value is not NaN, otherwise returns an error message.
 */
function validateNotNan(value: number | string | null): true | string {
  if (value) {
    // Validate that the value is not NaN
    const parsedNumber = parseIntOrFloat(value)
    if (!parsedNumber || !isNaN(parsedNumber)) {
      return true
    } else {
      return 'Please enter a value'
    }
  } else {
    return 'Please enter a value'
  }
}

/**
 * Validates that the input value is not zero.
 *
 * @param {number|string|null} value - The value to be validated.
 * @return {true|string} Returns true if the value is not zero, otherwise returns an error message.
 */
function validateNotZero(value: number | string | null): true | string {
  if (value) {
    // Validate that the value is not 0
    const parsedNumber = parseIntOrFloat(value)
    if (parsedNumber && parsedNumber !== 0) {
      return true
    } else {
      return 'Please enter a value greater than 0'
    }
  } else {
    return 'Please enter a value'
  }
}

// Display helpers

/**
 * Calculates the battery age based on the state of health (SoH).
 *
 * @return {string} A message describing the battery age.
 */
function batteryAgeMessage(): string {
  const degradedPercentage = 100 - SoH.value
  if (degradedPercentage >= 70) return 'this looks like a poorly functioning battery'
  const yearsOld = Math.floor(degradedPercentage / 3)
  if (yearsOld <= 0) return 'equivalent to a new battery'
  return 'equivalent to a ' + yearsOld + ' year old battery with normal usage'
}

/**
 * Returns the source URL of the image associated with the vehicle.value.
 *
 * @return {string} The source URL of the image.
 */
function imageSrc(): string {
  if (vehicle) {
    // get uploaded image to car in first instance.
    const images = vehicle.value?.imageSrcPaths
    if (images && images[0]) return images[0]
    // get model image in second instance.
    if (vehicle.value?.evModel)
      return vehicle.value.evModel?.imageSrc ?? getAssetSrc('car_images/placeholder_car.png')
    // get company logo image in third instance.
    const company = store.state.fleet
    if (company && company.logoSrc) {
      return company.logoSrc
    }
  }
  // get default image in fourth instance.
  return getAssetSrc('car_images/placeholder_car.png')
}

// data setup functions

/**
 * Initializes the component's data properties with default values from the vehicle object.
 *
 * @return {void} No return value.
 */
function setInitialValues(): void {
  loading.value = true
  dirty.value = false
  if (vehicle.value) {
    // ids
    name.value = vehicle.value.name ?? null
    plate.value = vehicle.value.licensePlate ?? null
    // weights
    vehicleWeight.value = vehicle.value.mass
    additionalWeight.value = 0
    // fuel
    fuelType.value = vehicle.value.fuelType ?? 'Petrol'
    batterySize.value = vehicle.value.batterySize
    SoH.value = vehicle.value.stateOfHealth ? vehicle.value.stateOfHealth * 100 : 100
    // charging
    acRating.value = vehicle.value.maxElectricPowerAC
    dcRating.value = vehicle.value.maxElectricPowerDC
    // energy
    energyUsedWhenStopped.value = 0
    energyUsedWhenMoving.value = 0
    // physics profile
    makeAndModel.value = vehicle.value.evModel ?? null
    aerodynamics.value = vehicle.value.dragCoefficient * 100
    tyreDynamics.value = vehicle.value.rollingResistanceCoefficient * 1000
    regenerativeBraking.value = vehicle.value.regenerativeBreakingEfficiency * 100
    powerTrain.value = vehicle.value.powerChainEfficiency * 100

    // get vehicle to update data from orakle
    vehicle.value.updateVehicleData()
  }
  selectedConnectors.value = getInitialConnectors()
  nextTick(() => {
    loading.value = false
  })
}

/**
 * Retrieves the initial connectors for a vehicle based on its user-selected plugs or EV model.
 *
 * @return {LocalConnectorData[]} An array of LocalConnectorData objects representing the initial connectors.
 */
function getInitialConnectors(): LocalConnectorData[] {
  if (vehicle) {
    if (vehicle.value?.userSelectedPlugs && vehicle.value.userSelectedPlugs.length)
      return getUserSelectedConnectors(vehicle.value.userSelectedPlugs)
    if (vehicle.value?.evModel) return getModelConnectors(vehicle.value.evModel)
  }
  return []
}

/**
 * Converts an array of user selected plugs into an array of LocalConnectorData objects.
 *
 * @param {UserSelectedPlug[]} userSelectedPlugs - An array of user selected plugs to be converted.
 * @return {LocalConnectorData[]} An array of LocalConnectorData objects representing the user selected connectors.
 */
function getUserSelectedConnectors(
  userSelectedPlugs: DirectusUserSelectedPlug[],
): LocalConnectorData[] {
  const tempArray: LocalConnectorData[] = []
  userSelectedPlugs.forEach((connector) => {
    const extraData = connectorDetailsDataMap.get(connector.standard)
    if (extraData) {
      tempArray.push({
        format: connector.format,
        standard: connector.standard,
        displayName: extraData.displayName,
        iconURL: getSrc(extraData.iconURL, connector.format === 'CABLE' ? 'cable' : 'socket'),
      })
    }
  })
  return tempArray
}

/**
 * Returns an array of LocalConnectorData objects representing the model's compatible connectors.
 *
 * @param {EVModel} model - The EV model to retrieve connectors for.
 * @return {LocalConnectorData[]} An array of LocalConnectorData objects.
 */
function getModelConnectors(model: EVModel): LocalConnectorData[] {
  const tempArray: LocalConnectorData[] = []
  model.compatibleConnectors.forEach((connector) => {
    const extraData = connectorDetailsDataMap.get(connector.standard)
    const alreadyAdded = !!tempArray.find((dataItem) => dataItem.standard === connector.standard)
    if (extraData && !alreadyAdded) {
      tempArray.push({
        format: 'CABLE',
        standard: connector.standard,
        displayName: extraData.displayName,
        iconURL: getSrc(extraData.iconURL, 'cable'),
      })
    }
  })
  return tempArray
}

/**
 * Returns the fallback image source URL.
 *
 * @return {string} The fallback image source URL.
 */
function fallbackImgSrc(): string {
  return getAssetSrc('plug_images/Empty-state-plug-02.svg')
}

/**
 * Converts the selected connectors to an array of user selected plugs.
 *
 * @return {UserSelectedPlug[]} An array of user selected plugs.
 */
function convertSelectedConnectorsToUserSelectedPlugs(): DirectusUserSelectedPlug[] {
  const tempArray: DirectusUserSelectedPlug[] = []

  selectedConnectors.value.forEach((connector) => {
    const extraData = connectorDetailsDataMap.get(connector.standard)
    if (extraData) {
      tempArray.push({
        standard: connector.standard,
        format: connector.format,
        powerType: extraData.powerType,
      })
    }
  })
  return tempArray
}

// control functions

/**
 * Flags the component's data as dirty, indicating that it has been modified.
 *
 * @return {void} No return value.
 */
function flagAsDirty(): void {
  if (loading.value) return
  dirty.value = true
}

// event handlers

function parseModelFuelType(fuelType: 'PETROL' | 'ELECTRIC' | 'DIESEL' | 'HYDROGEN'): FuelType {
  switch (fuelType) {
    case 'DIESEL':
      return 'Diesel'
    case 'ELECTRIC':
      return 'Electric'
    case 'HYDROGEN':
      return 'Hydrogen'
    case 'PETROL':
      return 'Petrol'
  }
}

function handleModelSelect(val: EVModel | null): void {
  if (val) {
    fuelType.value = parseModelFuelType(val.fuelType)
    batterySize.value = val.batterySize
    vehicleWeight.value = val.mass
    aerodynamics.value = val.dragCoefficient
    tyreDynamics.value = val.rollingResistanceCoefficient
    regenerativeBraking.value = val.regenRecovery * 100 // convert from decimal percentage representation to whole number percentage representation.
    powerTrain.value = val.powerChainEfficiency * 100 // convert from decimal percentage representation to whole number percentage representation.
    acRating.value = val.maxElectricPowerAC
    dcRating.value = val.maxElectricPowerDC
    SoH.value = val.calcLinearDegradationSOH() * 100 // convert from decimal percentage representation to whole number percentage representation.
    // connectors
    const tempArray: LocalConnectorData[] = []
    val.compatibleConnectors.forEach((connector) => {
      const data = connectors.value.find((dataItem) => dataItem.standard === connector.standard)
      const alreadyAdded = !!tempArray.find((dataItem) => dataItem.standard === connector.standard)
      if (data && !alreadyAdded) tempArray.push(data)
    })
    selectedConnectors.value = tempArray
  }
}

async function handelReset(): Promise<void> {
  // guard clause.
  if (!vehicle) return
  // indicate async process has begun.
  processing.value = true
  // update DB.
  saveStatus.value = await vehicle.value?.updateDirectusData({
    soh: null, // remove user provided SOH.
    AdvancedConfig: null, // remove advanced config.
    UserSelectedPlugs: null, // remove user provided connectors.
  })
  // update global state.
  store.commit(MutationTypes.updateIndividualVehicle, vehicle)
  // indicate async process has completed.
  processing.value = false
  // clean up.
  setInitialValues()
  setTimeout(() => {
    saveStatus.value = undefined
  }, 2500)
}

/** Adds/removes connector from selected connectors list.
 *
 * @param connector the target `LocalConnectorData` object for the connector.
 */
function toggleConnectorSelect(connector: LocalConnectorData): void {
  flagAsDirty()
  if (connectorIsSelected(connector)) {
    // filter out matching connector from array
    selectedConnectors.value = selectedConnectors.value.filter((selectedConnector) => {
      if (selectedConnector.standard !== connector.standard) return true
      if (
        selectedConnector.standard === connector.standard &&
        selectedConnector.format !== connector.format
      )
        return true
      return false // assumes this will be the selected connector that matches both standard and format.
    })
  } else {
    // add connector to array
    selectedConnectors.value.push(connector)
  }
}

/**
 * Saves the vehicle data to the database and updates the global state.
 *
 * This function checks for changes in the vehicle's properties and updates the
 * corresponding fields in the database. It also updates the global state with
 * the latest vehicle data.
 *
 * @return {void} No return value.
 */
async function saveData(): Promise<void> {
  if (!vehicle.value) return
  processing.value = true
  const copyVehicle: Vehicle = vehicle.value
  const dataToUpdate: Partial<VehicleData> = {}
  const newAdvConf: DirectusAdvancedConfig = vehicle.value.advancedConfig
    ? { ...vehicle.value.advancedConfig }
    : {}
  // identification
  if (name.value !== vehicle.value.name) {
    dataToUpdate.name = name.value
  }
  if (plate.value !== vehicle.value.licensePlate) {
    dataToUpdate.rego = plate.value
  }
  // weights
  if (vehicleWeight.value && parseIntOrFloat(vehicleWeight.value) !== vehicle.value.mass) {
    newAdvConf.Mass = parseIntOrFloat(vehicleWeight.value)
  }
  // fuel
  if (fuelType.value !== (vehicle.value.fuelType ?? null)) {
    copyVehicle.fuelType = fuelType.value ?? undefined
    dataToUpdate.fuel_type = fuelType.value
  }
  if (batterySize.value && parseIntOrFloat(batterySize.value) !== vehicle.value.batterySize) {
    newAdvConf.BatterySize = parseIntOrFloat(batterySize.value)
  }
  if (
    SoH.value !== (vehicle.value.stateOfHealth ? vehicle.value.stateOfHealth! * 100 : undefined)
  ) {
    const convertedSOH = to2DP(SoH.value / 100)
    copyVehicle.userProvidedStateOfHealth = convertedSOH
    dataToUpdate.soh = convertedSOH
  }
  // charging
  if (acRating.value && parseIntOrFloat(acRating.value) !== vehicle.value.maxElectricPowerAC) {
    newAdvConf.MaxElectricPowerAc = parseIntOrFloat(acRating.value)
  }
  if (dcRating.value && parseIntOrFloat(dcRating.value) !== vehicle.value.maxElectricPowerDC) {
    newAdvConf.MaxElectricPowerDc = parseIntOrFloat(dcRating.value)
  }
  // energy
  if (energyUsedWhenStopped.value) {
    const parsedVal = parseIntOrFloat(energyUsedWhenStopped.value)
    if (parsedVal) {
      newAdvConf.EnergyUsedWhenStopped = energyUsedWhenStopped.value
    }
  }
  if (energyUsedWhenMoving.value) {
    const parsedVal = parseIntOrFloat(energyUsedWhenMoving.value)
    if (parsedVal) {
      newAdvConf.EnergyUsedWhenMoving = energyUsedWhenMoving.value
    }
  }
  // physics profile
  if (
    aerodynamics.value &&
    parseIntOrFloat(aerodynamics.value / 100) !== vehicle.value.dragCoefficient
  ) {
    newAdvConf.DragCoefficient = parseIntOrFloat(aerodynamics.value / 100)
  }

  if (
    tyreDynamics.value &&
    parseIntOrFloat(tyreDynamics.value / 1000) !== vehicle.value.rollingResistanceCoefficient
  ) {
    newAdvConf.RollingResistanceCoefficient = parseIntOrFloat(tyreDynamics.value / 1000)
  }
  if (regenerativeBraking.value) {
    const parsedVal = parseIntOrFloat(regenerativeBraking.value)
    if (parsedVal && parsedVal !== vehicle.value.rollingResistanceCoefficient * 100) {
      newAdvConf.RegenerativeBreakingEfficiency = to2DP(parsedVal / 100)
    }
  }
  if (powerTrain.value) {
    const parsedVal = parseIntOrFloat(powerTrain.value)
    if (parsedVal && parsedVal !== vehicle.value.powerChainEfficiency * 100) {
      newAdvConf.PowerChainEfficiency = to2DP(parsedVal / 100)
    }
  }
  // compile connectors and cables

  // check if just model defaults
  if (vehicle.value.evModel) {
    // find models default connectors
    const modelDefaultConnectors = getModelConnectors(vehicle.value.evModel)
    if (
      selectedConnectors.value.length !== modelDefaultConnectors.length ||
      !modelDefaultConnectors.every((connector) => connectorIsSelected(connector))
    ) {
      // ASSUME: not the same and needs override
      const userSelectedPlugs = convertSelectedConnectorsToUserSelectedPlugs()
      copyVehicle.userSelectedPlugs = userSelectedPlugs
      dataToUpdate.UserSelectedPlugs = userSelectedPlugs.map((obj) => JSON.stringify(obj))
    } else {
      // ASSUMES: is default for model and userSelectedPlugs can be removed.
      copyVehicle.userSelectedPlugs = undefined
      dataToUpdate.UserSelectedPlugs = null
    }
  } else {
    // ASSUME: just straight override as no model for comparison.
    const userSelectedPlugs = convertSelectedConnectorsToUserSelectedPlugs()
    copyVehicle.userSelectedPlugs = userSelectedPlugs
    dataToUpdate.UserSelectedPlugs = userSelectedPlugs.map((obj) => JSON.stringify(obj))
  }

  // check advanced config has any entries
  if (Object.keys(newAdvConf).length) {
    copyVehicle.advancedConfig = newAdvConf
    dataToUpdate.AdvancedConfig = newAdvConf
  }

  // update DB
  saveStatus.value = await copyVehicle.updateDirectusData(dataToUpdate)
  // update global state
  store.commit(MutationTypes.updateIndividualVehicle, copyVehicle)
  // indicate async process has completed
  processing.value = false
  // clean up
  setInitialValues()
  setTimeout(() => {
    saveStatus.value = undefined
  }, 2500)
}

onMounted(async () => {
  await nextTick()
  setInitialValues()
})

watch(
  () => vehicle,
  () => {
    setInitialValues()
  },
)

function viewOnMap() {
  if (!vehicle) return
  store.dispatch(ActionTypes.showFleetVehicle, vehicle)
}
</script>

<style scoped>
.btn-border {
  border: 0.5px solid #aeaeae;
}

* :deep(.v-slider--horizontal) {
  margin-left: unset;
  margin-right: unset;
}

* :deep(.v-slider__track-fill) {
  border-radius: 2px; /* override default slider border-radius */
}

* :deep(.v-slider__track-background) {
  border-radius: 2px; /* override default slider border-radius */
}

.svg_base {
  width: 56px;
  height: 56px;
}

.svg_selected {
  transform: scale(1.2);
}

.svg_unselected {
  opacity: 0.6;
}
</style>
